import { Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IQuizFormInput, IQuizFormValues } from '../../../quiz.types';
import { StyledFieldError } from '../../../styles';
import {
  StyledHiddenFileInput,
  StyledUploadDeleteButton,
  StyledUploadFileChip,
  StyledUploadFileName,
  StyledUploadIcon,
  StyledUploadNoneText,
  StyledUploadPromptText,
  StyledUploadSelectedLabel,
  StyledUploadZone,
} from './styles';

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_FORMATS = ['image/png', 'image/jpeg', 'application/pdf'];
const ACCEPTED_EXTENSIONS = '.png,.jpg,.jpeg,.pdf';

// ---------------------------------------------------------------------------
// Upload (file question)
// ---------------------------------------------------------------------------
export function UploadInput({ input }: { input: IQuizFormInput }): JSX.Element {
  const { t } = useTranslation();
  const { control, errors } = useFormContext<IQuizFormValues>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const error = (errors?.answers as any)?.[input.id]?.file?.message as
    | string
    | undefined;

  const validateFile = (file: File | null): true | string => {
    if (!file)
      return input.isRequired
        ? (t('quiz.form.errors.required') as string)
        : true;
    if (!ACCEPTED_FORMATS.includes(file.type))
      return t('quiz.form.errors.fileFormat') as string;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024)
      return t('quiz.form.errors.fileSize') as string;
    return true;
  };

  return (
    <Controller
      name={`answers.${input.id}.file`}
      control={control}
      defaultValue={null}
      rules={{ validate: validateFile as any }}
      render={({ onChange, value }) => (
        <div>
          <StyledUploadZone
            isDragOver={isDragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e: React.DragEvent) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e: React.DragEvent) => {
              e.preventDefault();
              setIsDragOver(false);
              const file = e.dataTransfer.files?.[0];
              if (file) onChange(file);
            }}
          >
            <StyledUploadIcon />
            <StyledUploadPromptText variant="body2">
              {t('quiz.form.upload.button')}
            </StyledUploadPromptText>
            <Typography variant="caption" color="textSecondary">
              {t('quiz.form.upload.hint')}
            </Typography>
            {value ? (
              <div>
                <StyledUploadSelectedLabel variant="caption">
                  {t('quiz.form.upload.selected', { name: (value as File).name })}
                </StyledUploadSelectedLabel>

                <StyledUploadFileChip>
                  <StyledUploadFileName variant="caption">
                    {(value as File).name}
                  </StyledUploadFileName>
                  <StyledUploadDeleteButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    aria-label={t('quiz.form.upload.delete') as string}
                  >
                    <DeleteIcon fontSize="large" />
                  </StyledUploadDeleteButton>
                </StyledUploadFileChip>
              </div>
            ) : (
              <StyledUploadNoneText variant="caption" color="textSecondary">
                {t('quiz.form.upload.none')}
              </StyledUploadNoneText>
            )}
          </StyledUploadZone>
          <StyledHiddenFileInput
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            onChange={(e) => onChange(e.target.files?.[0] ?? null)}
          />
          {error && <StyledFieldError>{error}</StyledFieldError>}
        </div>
      )}
    />
  );
}
